import { useEffect, useState, useCallback } from "react";
import { Image as ImageIcon, CheckCircle2, XCircle, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Loader, Empty } from "@/components/Page";
import { inputCls } from "./shared";

export function ProfilePicturesPanel() {
  const [pictures, setPictures] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState({});

  const load = useCallback(() => {
    setPictures(null);
    Promise.all([
      api.get("/profile-picture/admin/pending"),
      api.get("/profile-picture/admin/stats")
    ]).then(([pendingRes, statsRes]) => {
      setPictures(pendingRes.data.pictures || []);
      setStats(statsRes.data);
    }).catch(() => {
      setPictures([]);
      setStats(null);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (pictureId) => {
    setBusy(true);
    try {
      await api.post(`/profile-picture/admin/${pictureId}/approve`);
      toast.success("Profile picture approved");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async (pictureId) => {
    const reason = rejectReason[pictureId]?.trim();
    if (!reason) {
      toast.error("Please enter a rejection reason");
      return;
    }

    setBusy(true);
    try {
      await api.post(`/profile-picture/admin/${pictureId}/reject`, { reason });
      toast.success("Profile picture rejected");
      setRejectReason((prev) => {
        const updated = { ...prev };
        delete updated[pictureId];
        return updated;
      });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="mb-8" data-testid="admin-profile-pictures-panel">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <ImageIcon size={18} className="text-amber-500" />
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">
            Profile Avatar Moderation
          </h3>
        </div>
        {stats && (
          <div className="flex items-center gap-3 text-xs">
            <span className="px-2 py-1 bg-amber-950/30 border border-amber-800/40 text-amber-300 rounded-sm">
              {stats.pending_count} Pending
            </span>
            <span className="text-zinc-500">
              Today: {stats.approved_today} approved, {stats.rejected_today} rejected
            </span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-500">
              {stats.total_approved} total approved
            </span>
          </div>
        )}
      </div>

      {pictures === null ? (
        <Loader />
      ) : pictures.length === 0 ? (
        <Empty label="No pending profile pictures" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {pictures.map((pic) => (
            <div
              key={pic.id}
              data-testid={`admin-profile-picture-${pic.id}`}
              className="border border-amber-900/40 bg-zinc-950 rounded-sm overflow-hidden"
            >
              {/* Image Preview */}
              <div className="relative bg-zinc-900 aspect-square">
                <img
                  src={pic.image_url}
                  alt={`Upload by ${pic.user?.name || "Unknown"}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 text-[10px] text-zinc-300 rounded-sm">
                  {pic.dimensions?.width || "?"}×{pic.dimensions?.height || "?"}px
                </div>
              </div>

              {/* User Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-100 font-medium">
                      {pic.user?.name || "Unknown User"}
                    </span>
                    {pic.user?.vip && (
                      <span className="text-xs px-1.5 py-0.5 bg-amber-950/40 border border-amber-800/50 text-amber-300 rounded-sm">
                        VIP
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-[11px] text-zinc-500 space-y-0.5 mb-3">
                  <div>Uploaded: {fmt(pic.uploaded_at)}</div>
                  <div>
                    Size: {(pic.file_size / 1000).toFixed(1)} KB
                    {pic.file_size > 900000 && (
                      <AlertCircle
                        size={10}
                        className="inline ml-1 text-amber-500"
                        title="Large file size"
                      />
                    )}
                  </div>
                  {pic.original_filename && (
                    <div className="truncate" title={pic.original_filename}>
                      File: {pic.original_filename}
                    </div>
                  )}
                </div>

                {/* Rejection Reason Input */}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Rejection reason (if rejecting)..."
                    value={rejectReason[pic.id] || ""}
                    onChange={(e) =>
                      setRejectReason((prev) => ({
                        ...prev,
                        [pic.id]: e.target.value
                      }))
                    }
                    className={`${inputCls} text-xs py-1.5`}
                    data-testid={`reject-reason-${pic.id}`}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => approve(pic.id)}
                    disabled={busy}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-emerald-950/40 border border-emerald-800/50 text-emerald-300 hover:bg-emerald-900/40 hover:border-emerald-700 rounded-sm transition-colors disabled:opacity-50"
                    data-testid={`approve-${pic.id}`}
                  >
                    {busy ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={12} />
                    )}
                    Approve
                  </button>
                  <button
                    onClick={() => reject(pic.id)}
                    disabled={busy}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-red-950/40 border border-red-800/50 text-red-300 hover:bg-red-900/40 hover:border-red-700 rounded-sm transition-colors disabled:opacity-50"
                    data-testid={`reject-${pic.id}`}
                  >
                    {busy ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <XCircle size={12} />
                    )}
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
